import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Slider,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingUp as ProgressIcon,
  Flag as PriorityIcon,
  Category as CategoryIcon,
  DateRange as DateIcon,
  CheckCircle as CompletedIcon,
  Schedule as InProgressIcon,
  Warning as OverdueIcon,
  Star as WeightIcon
} from '@mui/icons-material';

interface Goal {
  id: number;
  title: string;
  description: string;
  category: string;
  priority: string;
  status: 'not_started' | 'in_progress' | 'completed' | 'overdue';
  progress_percentage: number;
  target_date: string;
  success_criteria: string;
  weight_percentage: number;
  linked_competencies: string[];
}

const GoalManagement: React.FC = () => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  
  const [goalData, setGoalData] = useState({
    title: '',
    description: '',
    category: 'performance',
    priority: 'medium',
    target_date: '',
    success_criteria: '',
    weight_percentage: 20,
    linked_competencies: [] as string[]
  });

  const [progressData, setProgressData] = useState<{
    progress_percentage: number;
    status: 'not_started' | 'in_progress' | 'completed' | 'overdue';
    notes: string;
  }>({
    progress_percentage: 0,
    status: 'not_started',
    notes: ''
  });

  const competencies = [
    'Communication',
    'Leadership',
    'Problem Solving',
    'Teamwork',
    'Technical Skills',
    'Customer Focus',
    'Innovation',
    'Time Management'
  ];

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    // Mock data
    setGoals([
      {
        id: 1,
        title: 'Increase Sales Revenue by 15%',
        description: 'Focus on new client acquisition and upselling existing accounts to achieve 15% revenue growth',
        category: 'performance',
        priority: 'high',
        status: 'in_progress',
        progress_percentage: 75,
        target_date: '2024-03-31',
        success_criteria: 'Achieve $150,000 in additional revenue through new clients (60%) and upselling (40%)',
        weight_percentage: 30,
        linked_competencies: ['Communication', 'Customer Focus']
      },
      {
        id: 2,
        title: 'Complete React Certification',
        description: 'Obtain React Developer certification to enhance technical skills and career prospects',
        category: 'development',
        priority: 'medium',
        status: 'in_progress',
        progress_percentage: 45,
        target_date: '2024-02-28',
        success_criteria: 'Pass React certification exam with score of 85% or higher',
        weight_percentage: 20,
        linked_competencies: ['Technical Skills', 'Innovation']
      },
      {
        id: 3,
        title: 'Improve Team Collaboration',
        description: 'Lead cross-functional projects and mentor junior team members to improve overall team performance',
        category: 'behavioral',
        priority: 'high',
        status: 'completed',
        progress_percentage: 100,
        target_date: '2024-01-31',
        success_criteria: 'Successfully lead 2 cross-functional projects and mentor 3 junior team members',
        weight_percentage: 25,
        linked_competencies: ['Leadership', 'Teamwork', 'Communication']
      }
    ]);
  };

  const handleCreateGoal = async () => {
    const newGoal: Goal = {
      id: Date.now(),
      ...goalData,
      status: 'not_started',
      progress_percentage: 0
    };

    setGoals(prev => [...prev, newGoal]);
    setCreateDialogOpen(false);
    resetGoalData();
  };

  const handleUpdateProgress = async () => {
    if (!selectedGoal) return;

    setGoals(prev => prev.map(goal => 
      goal.id === selectedGoal.id 
        ? { ...goal, ...progressData }
        : goal
    ));

    setProgressDialogOpen(false);
    setSelectedGoal(null);
    resetProgressData();
  };

  const resetGoalData = () => {
    setGoalData({
      title: '',
      description: '',
      category: 'performance',
      priority: 'medium',
      target_date: '',
      success_criteria: '',
      weight_percentage: 20,
      linked_competencies: []
    });
    setActiveStep(0);
  };

  const resetProgressData = () => {
    setProgressData({
      progress_percentage: 0,
      status: 'not_started',
      notes: ''
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'info';
      case 'overdue': return 'error';
      case 'not_started': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CompletedIcon />;
      case 'in_progress': return <InProgressIcon />;
      case 'overdue': return <OverdueIcon />;
      default: return <InProgressIcon />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const isOverdue = (targetDate: string, status: string) => {
    return new Date(targetDate) < new Date() && status !== 'completed';
  };

  const steps = [
    'Goal Details',
    'Success Criteria',
    'Competencies & Weight'
  ];

  return (
    <Box sx={{ p: 3, pt: 2 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        mt: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Goal Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Set, track, and achieve your performance objectives
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          size="large"
        >
          Create New Goal
        </Button>
      </Box>

      {/* Goal Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="primary">
                {goals.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Goals
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="success.main">
                {goals.filter(g => g.status === 'completed').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="info.main">
                {goals.filter(g => g.status === 'in_progress').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                In Progress
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h3" color="error.main">
                {goals.filter(g => isOverdue(g.target_date, g.status)).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overdue
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Goals List */}
      <Grid container spacing={3}>
        {goals.map((goal) => (
          <Grid item xs={12} md={6} key={goal.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {goal.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {goal.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Chip
                      label={goal.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(isOverdue(goal.target_date, goal.status) ? 'overdue' : goal.status) as any}
                      size="small"
                      icon={getStatusIcon(goal.status)}
                    />
                    <Chip
                      label={goal.priority.toUpperCase()}
                      color={getPriorityColor(goal.priority) as any}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Progress</Typography>
                    <Typography variant="body2">{goal.progress_percentage}%</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={goal.progress_percentage} 
                    sx={{ height: 8, borderRadius: 4 }}
                    color={isOverdue(goal.target_date, goal.status) ? 'error' : 'primary'}
                  />
                </Box>

                {/* Goal Details */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Category: {goal.category.charAt(0).toUpperCase() + goal.category.slice(1)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Due: {formatDate(goal.target_date)} • Weight: {goal.weight_percentage}%
                  </Typography>
                  {goal.linked_competencies.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {goal.linked_competencies.map(comp => (
                        <Chip key={comp} label={comp} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button 
                    size="small" 
                    startIcon={<ProgressIcon />}
                    onClick={() => {
                      setSelectedGoal(goal);
                      setProgressData({
                        progress_percentage: goal.progress_percentage,
                        status: goal.status,
                        notes: ''
                      });
                      setProgressDialogOpen(true);
                    }}
                  >
                    Update Progress
                  </Button>
                  <Box>
                    <IconButton size="small" onClick={() => {
                      setSelectedGoal(goal);
                      setEditDialogOpen(true);
                    }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create Goal Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Goal</DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} orientation="vertical" sx={{ mt: 2 }}>
            <Step>
              <StepLabel>Goal Details</StepLabel>
              <StepContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Goal Title *"
                      value={goalData.title}
                      onChange={(e) => setGoalData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Increase sales revenue by 15%"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description *"
                      multiline
                      rows={3}
                      value={goalData.description}
                      onChange={(e) => setGoalData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Detailed description of what you want to achieve..."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={goalData.category}
                        onChange={(e) => setGoalData(prev => ({ ...prev, category: e.target.value }))}
                      >
                        <MenuItem value="performance">Performance</MenuItem>
                        <MenuItem value="development">Development</MenuItem>
                        <MenuItem value="behavioral">Behavioral</MenuItem>
                        <MenuItem value="project">Project</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={goalData.priority}
                        onChange={(e) => setGoalData(prev => ({ ...prev, priority: e.target.value }))}
                      >
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="low">Low</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Target Date *"
                      type="date"
                      value={goalData.target_date}
                      onChange={(e) => setGoalData(prev => ({ ...prev, target_date: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Button onClick={() => setActiveStep(1)} disabled={!goalData.title || !goalData.description}>
                    Next
                  </Button>
                </Box>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Success Criteria</StepLabel>
              <StepContent>
                <TextField
                  fullWidth
                  label="Success Criteria *"
                  multiline
                  rows={4}
                  value={goalData.success_criteria}
                  onChange={(e) => setGoalData(prev => ({ ...prev, success_criteria: e.target.value }))}
                  placeholder="Define specific, measurable criteria for success..."
                  helperText="Be specific about what success looks like and how it will be measured"
                />
                <Box sx={{ mt: 2 }}>
                  <Button onClick={() => setActiveStep(0)} sx={{ mr: 1 }}>
                    Back
                  </Button>
                  <Button onClick={() => setActiveStep(2)} disabled={!goalData.success_criteria}>
                    Next
                  </Button>
                </Box>
              </StepContent>
            </Step>

            <Step>
              <StepLabel>Competencies & Weight</StepLabel>
              <StepContent>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Linked Competencies
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {competencies.map(comp => (
                        <FormControlLabel
                          key={comp}
                          control={
                            <Checkbox
                              checked={goalData.linked_competencies.includes(comp)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setGoalData(prev => ({
                                    ...prev,
                                    linked_competencies: [...prev.linked_competencies, comp]
                                  }));
                                } else {
                                  setGoalData(prev => ({
                                    ...prev,
                                    linked_competencies: prev.linked_competencies.filter(c => c !== comp)
                                  }));
                                }
                              }}
                            />
                          }
                          label={comp}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Goal Weight: {goalData.weight_percentage}%
                    </Typography>
                    <Slider
                      value={goalData.weight_percentage}
                      onChange={(e, value) => setGoalData(prev => ({ ...prev, weight_percentage: value as number }))}
                      min={5}
                      max={50}
                      step={5}
                      marks
                      valueLabelDisplay="auto"
                    />
                    <Typography variant="caption" color="text.secondary">
                      How important is this goal relative to your other objectives?
                    </Typography>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                  <Button onClick={() => setActiveStep(1)} sx={{ mr: 1 }}>
                    Back
                  </Button>
                  <Button variant="contained" onClick={handleCreateGoal}>
                    Create Goal
                  </Button>
                </Box>
              </StepContent>
            </Step>
          </Stepper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            resetGoalData();
          }}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Progress Dialog */}
      <Dialog open={progressDialogOpen} onClose={() => setProgressDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Goal Progress</DialogTitle>
        <DialogContent>
          {selectedGoal && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedGoal.title}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Progress: {progressData.progress_percentage}%
                </Typography>
                <Slider
                  value={progressData.progress_percentage}
                  onChange={(e, value) => setProgressData(prev => ({ ...prev, progress_percentage: value as number }))}
                  min={0}
                  max={100}
                  step={5}
                  marks
                  valueLabelDisplay="auto"
                />
              </Box>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={progressData.status}
                  onChange={(e) => setProgressData(prev => ({ ...prev, status: e.target.value as any }))}
                >
                  <MenuItem value="not_started">Not Started</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Progress Notes"
                multiline
                rows={3}
                value={progressData.notes}
                onChange={(e) => setProgressData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Add notes about your progress..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProgressDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateProgress}>
            Update Progress
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GoalManagement;
